import { Outlet, Navigate, useLocation, matchPath, useNavigationType, Link } from "react-router-dom";
import { useEffect, useRef, useState, lazy, Suspense } from "react";
import { AlertTriangle } from "lucide-react";
import BottomNav from "@/components/BottomNav";
import Spinner from "@/components/Spinner";
import { useAuth } from "@/lib/auth";
import WhatsAppStep from "@/components/WhatsAppStep";
import { isNativeApp } from "@/lib/native";
import { shouldPromptForReview, markPrompted } from "@/lib/reviewPrompt";

// Lazy so the ReviewPromptModal chunk only downloads on the FIRST time
// we actually need to show it — keeps AppShell's initial payload lean.
const ReviewPromptModal = lazy(() => import("@/components/ReviewPromptModal"));

// Routes that should NOT show the bottom nav (immersive/edit flows).
const NAV_HIDDEN_ROUTES = [
  "/app/add",
  "/app/recipes/new",
  "/app/recipes/:rid",
];

/**
 * Scroll restoration for SPA back navigation:
 *   • Save scroll position of each pathname in sessionStorage on unload.
 *   • On POP (back/forward) restore it. On PUSH (new page) reset to top.
 */
function useScrollRestore() {
  const loc = useLocation();
  const navType = useNavigationType(); // POP | PUSH | REPLACE
  const KEY = "calofay:scroll:";
  const lastPath = useRef(loc.pathname);

  useEffect(() => {
    // Persist scroll on every scroll event. We deliberately do NOT save on
    // unmount: when the user taps a recipe card, the lazy-loaded detail route
    // triggers a Suspense boundary that briefly shrinks the page to just the
    // loader, causing the browser to clamp scrollY to 0 BEFORE our cleanup
    // fires. Saving that clamped 0 would overwrite the real position we
    // captured moments earlier and snap the user back to the top on return —
    // which is exactly what users were reporting on Android Chrome.
    const save = () => {
      const y = window.scrollY || 0;
      // Guard: never overwrite a larger stored value with 0. Real user
      // scrolls to top are captured before this handler runs; a lone 0 is
      // almost always a layout-driven clamp we should ignore.
      if (y === 0) {
        const cur = sessionStorage.getItem(KEY + lastPath.current);
        if (cur && Number(cur) > 0) return;
      }
      try { sessionStorage.setItem(KEY + lastPath.current, String(y)); } catch {}
    };
    window.addEventListener("scroll", save, { passive: true });
    return () => window.removeEventListener("scroll", save);
  }, [loc.pathname]);

  useEffect(() => {
    lastPath.current = loc.pathname;
    if (navType === "POP") {
      const raw = sessionStorage.getItem(KEY + loc.pathname);
      const y = raw ? parseInt(raw, 10) : 0;
      if (!y) return;
      // Restore the previous scroll position — but do it reactively as the
      // page grows, not on a fixed timer. The user reported being snapped to
      // the top when returning to وصفات المنصة on slow mobile networks; that
      // happened because recipe-card images kept the page short for >3 s and
      // our old rAF-limited retry gave up. ResizeObserver fires the moment
      // any child grows, so we can keep re-attempting the scroll every time
      // more content arrives without wasting frames polling.
      let done = false;
      const tryScroll = () => {
        if (done) return;
        const max = document.documentElement.scrollHeight - window.innerHeight;
        if (max >= y) {
          window.scrollTo(0, y);
          done = true;
          try { ro.disconnect(); } catch {}
        }
      };
      tryScroll();
      const ro = new ResizeObserver(tryScroll);
      ro.observe(document.documentElement);
      // Absolute stop after 15 s so we don't observe forever on truly
      // stuck pages (rare but avoids leaking the observer).
      const stop = setTimeout(() => { done = true; try { ro.disconnect(); } catch {} }, 15000);
      return () => { done = true; clearTimeout(stop); try { ro.disconnect(); } catch {} };
    } else if (navType === "PUSH") {
      // Fresh navigation into a new page — jump to the top.
      // Notably we do NOT scroll on REPLACE: pages like the recipes list
      // sync their tab/filter state to the URL via setSearchParams(..., {replace}),
      // which fires this effect with navType=REPLACE. Scrolling to 0 there
      // would clobber the user's current scroll on every filter change and
      // (worse) on re-mount after a back navigation.
      window.scrollTo(0, 0);
    }
  }, [loc.pathname, navType]);
}

export default function AppShell() {
  const { user, loading } = useAuth();
  const loc = useLocation();
  useScrollRestore();
  // Periodic review prompt — replaces the "ادعمنا بتقييمك" card that
  // used to live in Settings. Every time the user navigates to the Home
  // route on native, we consult `shouldPromptForReview()` (meal-count +
  // cooldown + lifetime cap all encoded in /lib/reviewPrompt.js). Once
  // the user rates 5★ we permanently silence the popup on this install.
  const [reviewOpen, setReviewOpen] = useState(false);
  useEffect(() => {
    if (!user) return;
    if (!isNativeApp()) return;              // web users are handled elsewhere
    if (loc.pathname !== "/app") return;      // only nudge on Home
    if (!shouldPromptForReview()) return;
    // Small delay so the popup doesn't slam over the tab-swap animation.
    const t = setTimeout(() => {
      setReviewOpen(true);
      markPrompted();
    }, 1200);
    return () => clearTimeout(t);
  }, [loc.pathname, user]);
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  // Admins / nutritionists that land on the user app (either via a
  // bookmark, a deep link, or the older `/app` hard-code in PublicOnly)
  // belong on the admin dashboard, not staring at their own
  // subscription paywall.
  if (user.role && user.role !== "user") return <Navigate to="/admin" replace />;
  // Subscription gate — if the user's subscription has lapsed we route them
  // to a friendly renewal screen instead of the full app. All their data is
  // preserved untouched — they just can't use the tools until they resub.
  // `active`, `trialing` and `past_due` (still within Stripe's grace window)
  // all count as "usable". `cancelled` / `expired` / `refunded` do not.
  const usable = ["active", "trialing", "past_due"].includes(user.subscription_status);
  // Never paid on web → the normal checkout funnel, not the "subscription expired" renewal page.
  if (!usable && !isNativeApp() && ["none", "", null, undefined].includes(user.subscription_status)) {
    return <Navigate to="/checkout?coupon=NEW10" replace />;
  }
  if (!usable && loc.pathname !== "/app/renew") {
    return <Navigate to="/app/renew" replace />;
  }
  // Post-purchase questions only after payment.
  if (!user.onboarding_completed && usable) return <Navigate to="/onboarding" replace />;

  const hideNav = NAV_HIDDEN_ROUTES.some((pattern) => matchPath({ path: pattern, end: true }, loc.pathname));
  // `past_due` means Stripe tried to charge and failed — user is inside a
  // ~7-day retry window. We keep the app usable but nudge them with a top
  // banner so they can renew before the account fully lapses.
  // Required WhatsApp number once the user is a paying subscriber.
  if (user.role !== "admin" && !user.phone && ["active", "trialing", "past_due"].includes(user.subscription_status)) {
    return <WhatsAppStep />;
  }
  const showPastDueBanner = user.subscription_status === "past_due"
    && !loc.pathname.startsWith("/app/renew");
  return (
    <div className="min-h-screen bg-transparent">
      <div className={`max-w-md mx-auto min-h-screen bg-transparent relative ${hideNav ? "pb-4" : "pb-24"}`}>
        {showPastDueBanner && (
          <Link
            to="/app/renew"
            className="block sticky top-0 z-30 bg-red-600 text-white px-4 py-2.5 text-xs font-semibold flex items-center gap-2 shadow-md"
            data-testid="past-due-banner"
          >
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span className="flex-1">بطاقتك رفضت الدفعة الأخيرة — اضغط هنا لتحديث وسيلة الدفع قبل توقف الاشتراك</span>
          </Link>
        )}
        <Outlet />
      </div>
      {!hideNav && !loc.pathname.startsWith("/app/renew") && <BottomNav />}
      {reviewOpen && (
        <Suspense fallback={null}>
          <ReviewPromptModal open={reviewOpen} onClose={() => setReviewOpen(false)} />
        </Suspense>
      )}
    </div>
  );
}

export function FullPageLoader() {
  // Uses the branded <Spinner size="lg" /> so the boot loader, the code-split
  // Suspense fallback and any in-app spinner all look identical — no jarring
  // "generic ring for 200ms, then Calofay dumbbell" flash.
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center cf-boot-bg" data-testid="full-page-loader">
      <Spinner size="lg" bare />
    </div>
  );
}
